import { Locale } from "@dealer-center/ddc-core-utils";
import type {
  ResetPasswordErrorReason,
  SaveUserInfoErrorReason
} from "@dealer-center/ddc-pages-configuration-interface";

/** Total over the reason union, so a new reason without copy is a type error. */
const SAVE_ERROR_LABELS: Record<SaveUserInfoErrorReason, string> = {
  LINKED_USER_IDENTITY: "UM_SAVE_ERR_LINKED",
  NOT_FOUND: "UM_SAVE_ERR_NOT_FOUND",
  VALIDATION: "UM_SAVE_ERR_INVALID",
  INVALID: "UM_SAVE_ERR_INVALID",
  UPSTREAM: "UM_SAVE_ERR_GENERIC"
};

/** First-person copy where the shared copy says "this user"; other reasons use the shared key. */
const OPERATOR_SAVE_ERROR_LABELS: Partial<
  Record<SaveUserInfoErrorReason, string>
> = {
  LINKED_USER_IDENTITY: "MI_SAVE_ERR_LINKED",
  NOT_FOUND: "MI_SAVE_ERR_NOT_FOUND"
};

/** `INVALID` is excluded: its copy is upstream's own message. */
const RESET_ERROR_LABELS: Record<
  Exclude<ResetPasswordErrorReason, "INVALID">,
  string
> = {
  LINKED_USER_PASSWORD: "UM_PW_ERR_LINKED",
  NOT_IN_ACCOUNT: "UM_PW_ERR_NOT_IN_ACCOUNT",
  NOT_FOUND: "UM_PW_ERR_NOT_FOUND",
  VALIDATION: "UM_PW_ERR_GENERIC",
  UPSTREAM: "UM_PW_ERR_GENERIC"
};

const OPERATOR_RESET_ERROR_LABELS: Partial<
  Record<Exclude<ResetPasswordErrorReason, "INVALID">, string>
> = {
  LINKED_USER_PASSWORD: "PW_BRIDGE",
  NOT_IN_ACCOUNT: "MI_PW_ERR_NOT_IN_ACCOUNT",
  NOT_FOUND: "MI_PW_ERR_NOT_FOUND"
};

/**
 * Read off `unknown`: a network `TypeError` or the shared 403 carries no `reason`, and an
 * unrecognized string must not index the tables.
 */
export const knownReason = <TReason extends string>(
  error: unknown,
  labels: Record<TReason, string>
): TReason | undefined => {
  const reason =
    error && typeof error === "object" && "reason" in error
      ? error.reason
      : undefined;

  return typeof reason === "string" &&
    Object.prototype.hasOwnProperty.call(labels, reason)
    ? (reason as TReason)
    : undefined;
};

/** An `INVALID` rejection's own message, shown as-is (English-only, not localized). */
export const invalidMessage = (error: unknown): string | undefined =>
  error &&
  typeof error === "object" &&
  "reason" in error &&
  error.reason === "INVALID" &&
  "message" in error &&
  typeof error.message === "string" &&
  error.message
    ? error.message
    : undefined;

export interface WriteErrorMessageOptions {
  /** Selects the copy register: first person only when the subject is the operator. */
  isSubjectTheOperator?: boolean;
}

export const saveErrorMessage = (
  error: unknown,
  { isSubjectTheOperator = false }: WriteErrorMessageOptions = {}
): string => {
  const reason = knownReason(error, SAVE_ERROR_LABELS);
  const operatorLabel =
    isSubjectTheOperator && reason
      ? OPERATOR_SAVE_ERROR_LABELS[reason]
      : undefined;

  return Locale.getText(
    operatorLabel ??
      (reason ? SAVE_ERROR_LABELS[reason] : "UM_SAVE_ERR_GENERIC")
  ) as string;
};

export const resetErrorMessage = (
  error: unknown,
  { isSubjectTheOperator = false }: WriteErrorMessageOptions = {}
): string => {
  const upstreamText = invalidMessage(error);
  if (upstreamText) {
    return upstreamText;
  }

  const reason = knownReason(error, RESET_ERROR_LABELS);
  const operatorLabel =
    isSubjectTheOperator && reason
      ? OPERATOR_RESET_ERROR_LABELS[reason]
      : undefined;

  return Locale.getText(
    operatorLabel ?? (reason ? RESET_ERROR_LABELS[reason] : "UM_PW_ERR_GENERIC")
  ) as string;
};
